<template>
  <div>
    <v-list v-if="items.length || empty" dense class="py-0">
      <v-list-item
        v-for="(item, idx) in items"
        :key="item.id_playlist_item"
        class="playlist-item px-4"
        :class="{
          'playlist-item--on-air': item.flg_no_ar,
          'playlist-item--previewing': !item.flg_no_ar && item.id_playlist_item === selectedPreviewId,
          'playlist-item--drag-over': dragOverIdx === idx,
        }"
        :draggable="!readonly"
        @dragstart="onDragStart($event, idx)"
        @dragover.prevent="onDragOver($event, idx)"
        @dragleave="onDragLeave"
        @drop="onDrop($event, idx)"
        @dragend="onDragEnd"
      >
        <v-icon v-if="!readonly" small class="drag-handle mr-2" style="cursor: grab">mdi-drag-vertical</v-icon>
        <v-list-item-avatar
          tile
          width="80"
          height="56"
          class="playlist-item__thumb mr-3"
          title="Clique para pré-visualizar (sem colocar no ar)"
          @click="selectForPreview(item)"
        >
          <v-img
            v-if="itemThumb(item)"
            :key="itemThumb(item)"
            :src="itemThumb(item)"
            contain
            @error="onThumbError(item.id_playlist_item)"
          >
            <template #placeholder>
              <div class="d-flex align-center justify-center fill-height">
                <v-progress-circular indeterminate size="16" width="2" color="primary" />
              </div>
            </template>
          </v-img>
          <v-icon v-else small color="text_secondary">mdi-image-outline</v-icon>
        </v-list-item-avatar>
        <v-list-item-content
          class="py-1 playlist-item__body"
          title="Clique para pré-visualizar (sem colocar no ar)"
          @click="selectForPreview(item)"
        >
          <v-list-item-title class="playlist-item__name">
            {{ item.cena && item.cena.dsc_nome_cena || 'Cena sem nome' }}
          </v-list-item-title>
        </v-list-item-content>
        <v-chip
          v-if="item.editadoOperador"
          x-small color="amber darken-2" dark class="mr-1"
          title="Editado pelo operador — tem precedência até a próxima atualização da redação"
        >
          <v-icon x-small left>mdi-account-edit</v-icon>Editado
        </v-chip>
        <v-chip
          v-if="syncBadge(item)"
          x-small :color="syncBadge(item).color" dark class="mr-1"
          :title="syncBadge(item).title"
        >{{ syncBadge(item).label }}</v-chip>
        <span v-if="item.flg_no_ar" class="pl-badge pl-badge--onair mr-1">
          <v-icon size="10" class="mr-1">mdi-circle</v-icon>ON AIR
        </span>
        <span v-else-if="item.id_playlist_item === selectedPreviewId" class="pl-badge pl-badge--pvw mr-1">
          <v-icon size="10" class="mr-1">mdi-circle</v-icon>PVW
        </span>
        <v-btn
          v-if="showItemTakeButton && isItemTakeable(item)"
          icon x-small class="gg-take-btn mr-1"
          @click="$emit('take-item', item)"
          title="Colocar no ar (take deste item)"
        >
          <v-icon small>mdi-play-circle</v-icon>
        </v-btn>
        <v-menu v-if="groups.length && !readonly" offset-y>
          <template #activator="{ on, attrs }">
            <v-btn icon x-small v-on="on" v-bind="attrs" title="Mover para pasta">
              <v-icon x-small>mdi-folder-move-outline</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-subheader class="text-caption py-0">Mover para</v-subheader>
            <v-list-item v-if="currentGrupo !== null" @click="$emit('move', { item, idGrupo: null })">
              <v-list-item-title class="text-body-2">Sem pasta (topo)</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="g in otherGroups"
              :key="g.id_grupo"
              @click="$emit('move', { item, idGrupo: g.id_grupo })"
            >
              <v-list-item-title class="text-body-2">{{ g.dsc_titulo }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn v-if="!readonly" icon x-small @click="$emit('remove', item)" title="Remover">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </v-list-item>

      <div v-if="empty" class="sc-empty-state">
        <span class="sc-empty-state__title">Playlist vazia</span>
      </div>
    </v-list>
  </div>
</template>

<script>
/**
 * Draggable list of playlist items. Drag state is leaf-local (`dragIdx`,
 * `dragOverIdx`) but the parent is notified on drag start/end so it can
 * pause polling that would otherwise overwrite the in-flight drag.
 *
 * Emits:
 *   - drag-state-change(isDragging: boolean) — parent gates polling
 *   - reorder(newItems: array)               — parent persists + updates state
 *   - remove(item)                            — parent dispatches store action
 */
import { mapGetters } from 'vuex'
import { playoutItemPreviewUrl, playoutItemMosThumbnailUrl } from '@/utils/sceneUtils'
import { isItemTakeable as itemIsTakeable } from '@/utils/playoutUtils'
import { getVizrtThumbnailUrl } from '@/store/modules/globographics'

// Per-row take button: hidden for V2. UX/UI didn't find a per-item take suitable
// alongside the ControlBar, which is where taking is driven from — but the feature
// is only HIDDEN, not withdrawn. Everything behind it is intact (`take-item` →
// PlaylistGroupedView → PlayoutPlaylistPanel.handleTakeItem → playout/playlistTakeItem),
// so flipping this to true is the only change needed to bring it back.
const SHOW_ITEM_TAKE_BUTTON = false

export default {
  name: 'PlaylistItemList',
  props: {
    items: { type: Array, required: true },
    empty: { type: Boolean, default: false },
    // When grouping is in use, the parent passes the list of groups so each item
    // can offer a "move to story" menu. currentGrupo = the group this list renders
    // (null = top-level), excluded from the move targets.
    groups: { type: Array, default: () => [] },
    currentGrupo: { type: Number, default: null },
    // MOS-origin playlists are newsroom-authoritative: no drag-reorder, move or
    // remove — the operator only operates (take/next/out).
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      dragIdx: null,
      dragOverIdx: null,
      // idItem → cache-buster set after a successful lazy on-view render, so the
      // <img> reloads the freshly-warmed preview (the item's hash isn't known yet).
      previewBust: {},
      // idItem → true once a lazy render was requested, so we don't re-request it
      // on every poll while it renders (or if it stays unavailable).
      requestedPreviews: {},
      // idItem → true when its thumbnail <img> failed to load (e.g. a template
      // with no PDS thumbnail) → degrade to the icon instead of a broken image.
      thumbError: {},
    }
  },
  computed: {
    ...mapGetters({
      selectedPreviewId: 'playout/getterPreviewSelectionId',
    }),
    otherGroups() {
      return this.groups.filter(g => g.id_grupo !== this.currentGrupo)
    },
    showItemTakeButton() {
      return SHOW_ITEM_TAKE_BUTTON
    },
  },
  watch: {
    items: { handler: 'requestMissingPreviews', immediate: true },
  },
  methods: {
    // Select this item for the big preview (FE-local, no Take). Toggling the same
    // item off is handled in the store action. The big render appears in the
    // "Cena em Exibição" block; this never touches on-air state.
    selectForPreview(item) {
      this.$store.dispatch('playout/setPreviewSelection', item.id_playlist_item)
    },
    // Takeable, not published: the API publishes the head of a rundown and fills the
    // rest behind the response, publishing whatever the operator reaches first on the
    // way through the Take. Gating the button on published-ness would hide it for
    // exactly the items that deferred publishing was meant to keep reachable.
    isItemTakeable(item) {
      return itemIsTakeable(item)
    },
    // Inline (MOS) items have no cena (id_gh_cena null); their preview is served
    // per item id. Native items fall back to the template thumbnail.
    isMosItem(item) {
      return item && item.id_gh_cena == null
    },
    // A native item's template lives on its cena; a MOS item's on the item itself.
    itemTemplateId(item) {
      return item.dsc_vizrt_template_id || (item.cena && item.cena.dsc_vizrt_template_id) || null
    },
    // A thumbnail failed to load — flag it so itemThumb() returns null (fallback icon).
    onThumbError(id) {
      this.$set(this.thumbError, id, true)
    },
    itemThumb(item) {
      if (this.thumbError[item.id_playlist_item]) return null
      // Native items go straight to the template thumbnail — we never show a separate
      // item-render for them (it wouldn't hydrate the cena's image fields and would
      // replace the correct thumbnail with a wrong render).
      if (this.isMosItem(item)) {
        // MOS items: MSE snapshot (correct per-element scene context) → our own
        // render → template thumbnail.
        if (item.dsc_mos_thumbnail_url) return playoutItemMosThumbnailUrl(item)
        if (this.previewBust[item.id_playlist_item]) {
          return playoutItemPreviewUrl(item, this.previewBust[item.id_playlist_item])
        }
        if (item.dsc_preview_gcs_path) return playoutItemPreviewUrl(item)
      }
      // Fallback: the template's static (unfilled) thumbnail — never a black box.
      // null → the mdi-image-outline icon.
      return getVizrtThumbnailUrl(this.itemTemplateId(item))
    },
    // Lazy on-view fallback: for a visible item that has a template but was NEVER
    // warmed (no dsc_preview_hash), ask the API to render one (once), then reload
    // its <img>. An item whose warm already ran — image OR empty (hash set,
    // gcs_path null) — is left alone so an un-renderable template isn't retried.
    requestMissingPreviews() {
      for (const item of this.items) {
        const id = item.id_playlist_item
        // Only MOS/inline items get an on-demand render. Native items use their
        // cena's editor render (or the template thumbnail) — never a separate
        // item-render, which wouldn't hydrate the cena's GCS image fields.
        if (!this.isMosItem(item) || !this.itemTemplateId(item)) continue
        // Items with the MSE's own thumbnail use that — don't render our own.
        if (item.dsc_mos_thumbnail_url) continue
        if (item.dsc_preview_hash || item.dsc_preview_gcs_path || this.requestedPreviews[id]) continue
        this.$set(this.requestedPreviews, id, true)
        this.$store.dispatch('playout/renderPlaylistItemPreview', { idItem: id })
          .then((res) => {
            if (res && res.ready) this.$set(this.previewBust, id, Date.now())
          })
          .catch(() => {})
      }
    },
    // Newsroom-change badge (dsc_mos_sync_flag) — what the redação did since the
    // operator last acted. Distinct from the "Editado" (operator-edit) chip.
    syncBadge(item) {
      const map = {
        NEW: { label: 'Novo', color: 'primary', title: 'Novo item recebido da redação' },
        CHANGED: { label: 'Alterado', color: 'info', title: 'Conteúdo alterado na redação' },
        AR_CHANGED: { label: 'Falha no ar', color: 'orange darken-2', title: 'Alterado na redação enquanto no ar, mas o envio ao engine falhou — refaça o take para aplicar' },
        AR_REMOVED: { label: 'Removido na redação', color: 'red darken-2', title: 'Removido na redação enquanto no ar — tire do ar quando puder' },
      }
      return map[item && item.dsc_mos_sync_flag] || null
    },
    onDragStart(event, idx) {
      this.dragIdx = idx
      event.dataTransfer.effectAllowed = 'move'
      this.$emit('drag-state-change', true)
    },
    onDragOver(event, idx) {
      this.dragOverIdx = idx
      event.dataTransfer.dropEffect = 'move'
    },
    onDragLeave() {
      this.dragOverIdx = null
    },
    onDrop(event, targetIdx) {
      this.dragOverIdx = null
      if (this.dragIdx === null || this.dragIdx === targetIdx) {
        this.dragIdx = null
        this.$emit('drag-state-change', false)
        return
      }
      const next = [...this.items]
      const moved = next.splice(this.dragIdx, 1)[0]
      next.splice(targetIdx, 0, moved)
      this.dragIdx = null
      this.$emit('drag-state-change', false)
      this.$emit('reorder', next)
    },
    onDragEnd() {
      this.dragIdx = null
      this.dragOverIdx = null
      this.$emit('drag-state-change', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.playlist-item__thumb {
  border-radius: var(--sc-radius-sm);
  // Backing so a lower-third's real transparency reads (the render service only
  // forces opacity on fully-transparent frames, not genuine overlays).
  background: rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
  align-self: center;
}

.playlist-item__name {
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.06px;
  color: var(--sc-gg-text-secondary) !important;
}

.playlist-item {
  min-height: 72px;
  // Rows sit on the deepest surface so they read as inset against the folder header
  // (surface-2) that groups them — that two-level contrast is what separates a story
  // from its items at a glance. Hover lifts a row to the header's tone.
  background: var(--sc-gg-surface-3);
  // The leading pipe is always reserved (transparent) so lighting it up only
  // recolours the border — it never shifts the thumbnail/title.
  border-left: 3px solid transparent;
  border-bottom: 2px solid rgba(77, 78, 81, 0.3);
  transition: background-color var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background-color: var(--sc-gg-surface-2);
  }

  // On air = a red pipe at the row's leading edge only (no fill) — the selection
  // itself is carried by the ●ON AIR badge, matching the design.
  &--on-air {
    border-left-color: var(--sc-gg-error);
  }

  // Previewing = the same leading pipe in the PVW green (no fill). On air wins the
  // pipe, so the class is only applied when the item isn't also on air.
  &--previewing {
    border-left-color: var(--sc-gg-success);
  }

  &--drag-over {
    border-top: 2px solid var(--sc-gg-accent);
    background-color: rgba(var(--sc-gg-accent-rgb), 0.08);
  }
}

.playlist-item__body {
  cursor: pointer;
}

// Inline status indicators — ●PVW / ●ON AIR. Dot + label only (no pill/border),
// per the design.
.pl-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;

  .v-icon {
    color: inherit !important;
  }
}

.pl-badge--onair {
  color: var(--sc-gg-critical);
}

.pl-badge--pvw {
  color: var(--sc-gg-success);
}

.gg-take-btn ::v-deep .v-icon {
  color: var(--sc-gg-accent) !important;
}
</style>
